/**
 * Connectif Shared Data — Collection Handler
 * Fetches products from a Connectif "Share Data" node and prepends them
 * before the first item of the collection product list. No loading state
 * is shown; the collection renders normally while this runs in the background.
 *
 * Multiple blocks are supported. Each gets a placeholder inserted into the
 * product-list at init time so admin block order is always preserved,
 * regardless of which Connectif response arrives first.
 */

class ConnectifCollectionHandler {
  constructor(anchor, placeholder, productList) {
    this.anchor = anchor;
    this.placeholder = placeholder;
    this.productList = productList;
    this.identifier = anchor.getAttribute('data-connectif-collection-identifier');
    this.maxItems = parseInt(anchor.getAttribute('data-connectif-max-items'), 10) || 0;
    this.processed = false;

    if (!this.identifier) return;

    this.init();
  }

  init() {
    document.addEventListener('connectif.managed.shared_data_received', (event) => {
      if (event?.detail?.id === this.identifier) {
        this.getAndProcess();
      }
    });

    if (window.connectif?.managed) {
      setTimeout(() => this.getAndProcess(), 100);
    } else {
      this.waitForConnectif();
    }
  }

  waitForConnectif() {
    const check = setInterval(() => {
      if (window.connectif?.managed) {
        clearInterval(check);
        this.getAndProcess();
      }
    }, 100);
    setTimeout(() => {
      clearInterval(check);
      if (!this.processed) this.placeholder.remove();
    }, 10000);
  }

  getAndProcess(retryCount = 0) {
    if (this.processed) return;
    if (!window.connectif?.managed) return;

    const sharedData = window.connectif.managed.getSharedDataById(this.identifier);

    if (sharedData?.products?.length > 0) {
      this.processed = true;
      this.processProducts(sharedData.products);
    } else if (retryCount < 5) {
      setTimeout(() => this.getAndProcess(retryCount + 1), 200 * (retryCount + 1));
    } else {
      this.placeholder.remove();
    }
  }

  async processProducts(connectifProducts) {
    const source = this.maxItems > 0 ? connectifProducts.slice(0, this.maxItems) : connectifProducts;
    const handles = source
      .filter(p => p.productDetailUrl)
      .map(p => {
        try {
          const url = new URL(p.productDetailUrl);
          const parts = url.pathname.split('/');
          return parts[parts.length - 1].replace('.html', '');
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    if (!handles.length) {
      this.placeholder.remove();
      return;
    }

    const cards = await Promise.all(handles.map(h => this.fetchProductCard(h)));
    const validCards = cards.filter(Boolean);

    if (!validCards.length) {
      this.placeholder.remove();
      return;
    }

    // Replace the pre-positioned placeholder with the fetched cards.
    // Because placeholders were inserted in admin block order at init time,
    // this preserves order regardless of which response arrives first.
    const tpl = document.createElement('template');
    tpl.innerHTML = validCards.join('');
    this.placeholder.replaceWith(tpl.content);
  }

  async fetchProductCard(handle) {
    try {
      //const locale = window.Shopify?.locale;
      //const prefix = locale ? `/${locale}` : '';
      //const response = await fetch(`${prefix}/products/${handle}?sections=connectif-product-card`);
      const response = await fetch(`/products/${handle}?sections=connectif-product-card`);
      if (!response.ok) return null;

      const data = await response.json();
      const sectionHtml = data['connectif-product-card'];
      if (!sectionHtml?.trim()) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');

      // Strip the section wrapper (id="shopify-section-connectif-product-card") — it's the
      // same for every fetch, producing duplicate IDs. Return only its inner content.
      const sectionWrapper = doc.querySelector('[id^="shopify-section-"]');
      if (sectionWrapper) {
        return sectionWrapper.innerHTML.trim() || null;
      }

      return doc.body.innerHTML.trim() || null;
    } catch {
      return null;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function init() {
  const productList = document.querySelector('product-list');
  const anchors = [...document.querySelectorAll('[data-connectif-collection-identifier]')];

  if (!anchors.length || !productList) return;

  // Insert one placeholder per block in reverse order so that after all
  // afterbegin insertions the placeholders sit in the same order as the blocks.
  // display:contents makes them invisible to the grid (no empty cell) until replaced.
  const placeholders = anchors.map(() => {
    const ph = document.createElement('div');
    ph.style.display = 'contents';
    return ph;
  });
  [...placeholders].reverse().forEach(ph => productList.insertAdjacentElement('afterbegin', ph));

  anchors.forEach((anchor, i) => new ConnectifCollectionHandler(anchor, placeholders[i], productList));
}
